import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ClaimResponseType,
  ClaimsFromPlacesType,
  CommentsType,
  OrderProductType,
} from '../../actions/types'
import {
  createClaim,
  createClaimComments,
  deleteCurrentClaim,
  getAllClaims,
  getAllClaimsFromPlatforms,
  getClaimComments,
  getCurrentClaim,
  getCurrentClaimFromCurrentPlatform,
  getPagedClaims,
  getStatsClaims,
  changeClaimMainInformation,
  changeClaimDescription,
} from '../../actions/claimsAction'
import { WritableDraft } from 'immer/dist/internal'

export interface IClaims {
  allClaims: ClaimResponseType[]
  pagedClaims: ClaimResponseType[]
  statsClaims: ClaimResponseType[]
  allClaimsFromPlaces: ClaimsFromPlacesType[]
  currentClaim: ClaimResponseType
  currentClaimFromCurrentPlace: ClaimResponseType[]
  claimComments: CommentsType[]
  claimsDataForCheck: ClaimResponseType
  isLoading: boolean
  isLoadingComments: boolean
  error: unknown | string
  isLoadingPagedClaims: boolean
  isLoadingStatsClaims: boolean
}

const initialState: IClaims = {
  allClaims: [],
  pagedClaims: [],
  statsClaims: [],
  allClaimsFromPlaces: [],
  currentClaim: {
    peopleCount: '',
    description: '',
    startDate: '',
    endDate: '',
    serviceFeePercent: 0,
    discountPercent: 0,
    user: {
      id: 0,
      role: '',
      fullName: '',
      phone: '',
      email: '',
      login: '',
      password: '',
      employee: {
        id: 0,
        createdAt: '',
      },
      isBlocked: false,
    },
    place: {
      id: 0,
      name: '',
      city: '',
      address: '',
      size: 0,
      description: '',
      color: '',
      images: [],
      files: [],
      createdAt: '',
      isBlocked: false,
      establishment: {
        rentCost: 0,
        id: 0,
        name: '',
        address: '',
        size: '',
        images: [],
        company: {
          id: 0,
          name: '',
          role: '',
          city: '',
          address: '',
          contactFullName: '',
          contactPhone: '',
          email: '',
          inn: '',
          description: '',
          isBlocked: false,
          createdAt: '',
          comments: [],
          employees: [],
          images: [],
          logo: undefined,
        },
        place: {
          id: 0,
          name: '',
          city: '',
          address: '',
          size: 0,
          description: '',
          color: '',
          images: [],
          files: [],
          createdAt: '',
          company: {
            id: 0,
            name: '',
            role: '',
            city: '',
            address: '',
            contactFullName: '',
            contactPhone: '',
            email: '',
            inn: '',
            description: '',
            isBlocked: false,
            createdAt: '',
            comments: [],
            employees: [],
            images: [],
            logo: undefined,
          },
          isBlocked: false,
          establishment: undefined,
        },
      },
      company: {
        id: 0,
        name: '',
        role: '',
        city: '',
        address: '',
        contactFullName: '',
        contactPhone: '',
        email: '',
        inn: '',
        description: '',
        createdAt: '',
        isBlocked: false,
        comments: [],
        employees: [],
        images: [],
      },
    },
    id: 0,
    status: '',
    createdAt: '',
    comments: [],
    order: {
      id: 0,
      orderId: 0,
      productIds: [],
      totalPrice: 0,
      price: 0,
      orderDetails: [],
    },
  },
  claimComments: [],
  currentClaimFromCurrentPlace: [],
  claimsDataForCheck: {
    description: '',
    peopleCount: '',
    startDate: '',
    endDate: '',
    user: {
      id: 0,
      role: '',
      fullName: '',
      phone: '',
      email: '',
      login: '',
      password: '',
      employee: {
        id: 0,
        createdAt: '',
      },
      isBlocked: false,
    },
    place: {
      id: 0,
      name: '',
      city: '',
      address: '',
      size: 0,
      description: '',
      color: '',
      images: [],
      files: [],
      createdAt: '',
      isBlocked: false,
      company: {
        id: 0,
        name: '',
        role: '',
        city: '',
        address: '',
        contactFullName: '',
        contactPhone: '',
        email: '',
        inn: '',
        description: '',
        createdAt: '',
        isBlocked: false,
        employees: [],
        comments: [],
        images: [],
      },
    },
    id: 0,
    status: '',
    createdAt: '',
    comments: [],
    order: {
      id: 0,
      orderId: 0,
      productIds: [],
      totalPrice: 0,
      price: 0,
      orderDetails: [],
    },
  },
  isLoading: false,
  isLoadingComments: false,
  isLoadingPagedClaims: false,
  isLoadingStatsClaims: false,
  error: '',
}

export const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    clearError(state) {
      state.error = ''
    },

    updateOrderDetailQuantity(
      state,
      action: PayloadAction<{ claimId: number; orderDetailId: number; newQuantity: number }>,
    ) {
      const { claimId, orderDetailId, newQuantity } = action.payload

      const calculateTotalPrice = (claim: WritableDraft<ClaimResponseType>) => {
        const productsSum =
          claim.order?.orderDetails.reduce(
            (sum, detail) => sum + detail.price * detail.quantity,
            0,
          ) || 0

        const discount = (productsSum * (claim.discountPercent || 0)) / 100

        const serviceFee = (productsSum * (claim.serviceFeePercent || 0)) / 100

        const rent = claim.place?.establishment?.rentCost || 0

        const finalSum = productsSum - discount + serviceFee + rent

        return Math.round(finalSum)
      }
      const claimToUpdate = state.allClaims.find((claim) => claim.id === claimId)
      if (claimToUpdate?.order) {
        const detailToUpdate = claimToUpdate.order.orderDetails.find(
          (detail) => detail.productId === orderDetailId,
        )
        if (detailToUpdate) {
          detailToUpdate.quantity = newQuantity
          claimToUpdate.order.productIds = claimToUpdate.order.orderDetails
            .filter((d) => d.quantity > 0)
            .map((d) => d.productId)

          claimToUpdate.order.price = claimToUpdate.order.orderDetails.reduce(
            (sum, detail) => sum + detail.price * detail.quantity,
            0,
          )
          claimToUpdate.order.totalPrice = calculateTotalPrice(claimToUpdate)
        }
      }

      if (state.currentClaim.id === claimId && state.currentClaim.order) {
        const currentDetail = state.currentClaim.order.orderDetails.find(
          (detail) => detail.productId === orderDetailId,
        )
        if (currentDetail) {
          currentDetail.quantity = newQuantity
          state.currentClaim.order.productIds = state.currentClaim.order.orderDetails
            .filter((d) => d.quantity > 0)
            .map((d) => d.productId)

          state.currentClaim.order.price = state.currentClaim.order.orderDetails.reduce(
            (sum, detail) => sum + detail.price * detail.quantity,
            0,
          )
          state.currentClaim.order.totalPrice = calculateTotalPrice(state.currentClaim)
        }
      }
    },

    addOrderDetail(
      state,
      action: PayloadAction<{ claimId: number; orderDetail: OrderProductType }>,
    ) {
      const { claimId, orderDetail } = action.payload

      const calculateTotalPrice = (claim: WritableDraft<ClaimResponseType>) => {
        const productsSum =
          claim.order?.orderDetails.reduce(
            (sum, detail) => sum + detail.price * detail.quantity,
            0,
          ) || 0

        const discount = (productsSum * (claim.discountPercent || 0)) / 100

        const serviceFee = (productsSum * (claim.serviceFeePercent || 0)) / 100

        const rent = claim.place?.establishment?.rentCost || 0

        const finalSum = productsSum - discount + serviceFee + rent

        return Math.round(finalSum)
      }

      const initNewOrder = () => ({
        id: 0,
        orderId: 0,
        productIds: [orderDetail.productId],
        price: orderDetail.price * orderDetail.quantity,
        orderDetails: [orderDetail],
        totalPrice: 0,
      })

      const updateClaimOrder = (claim: WritableDraft<ClaimResponseType>) => {
        if (!claim.order) {
          claim.order = initNewOrder()
        } else {
          const existingIndex = claim.order.orderDetails.findIndex(
            (d) => d.productId === orderDetail.productId,
          )

          if (existingIndex >= 0) {
            claim.order.orderDetails[existingIndex].quantity += orderDetail.quantity
          } else {
            claim.order.orderDetails.push(orderDetail)
            claim.order.productIds.push(orderDetail.productId)
          }

          claim.order.price = claim.order.orderDetails.reduce(
            (sum, detail) => sum + detail.price * detail.quantity,
            0,
          )
        }

        claim.order.totalPrice = calculateTotalPrice(claim)
      }

      const claimToUpdate = state.allClaims.find((claim) => claim.id === claimId)
      if (claimToUpdate) {
        updateClaimOrder(claimToUpdate)
      }

      if (state.currentClaim.id === claimId) {
        updateClaimOrder(state.currentClaim)
      }
    },

    clearOrder(state, action: PayloadAction<{ claimId: number }>) {
      const { claimId } = action.payload
      const resetOrder = {
        id: 0,
        orderId: 0,
        productIds: [],
        price: 0,
        totalPrice: 0,
        orderDetails: [],
      }

      const claimToUpdate = state.allClaims.find((claim) => claim.id === claimId)
      if (claimToUpdate) {
        claimToUpdate.order = resetOrder
      }

      if (state.currentClaim.id === claimId) {
        state.currentClaim.order = resetOrder
      }
    },

    removeOrderDetail: (state, action: PayloadAction<{ claimId: number; productId: number }>) => {
      const { claimId, productId } = action.payload

      const calculateTotalPrice = (claim: WritableDraft<ClaimResponseType>) => {
        const productsSum =
          claim.order?.orderDetails.reduce(
            (sum, detail) => sum + detail.price * detail.quantity,
            0,
          ) || 0

        const discount = (productsSum * (claim.discountPercent || 0)) / 100
        const serviceFee = (productsSum * (claim.serviceFeePercent || 0)) / 100
        const rent = claim.place?.establishment?.rentCost || 0
        const finalSum = productsSum - discount + serviceFee + rent

        return Math.round(finalSum)
      }

      const updateClaimOrder = (claim: WritableDraft<ClaimResponseType>) => {
        if (claim.order) {
          claim.order.orderDetails = claim.order.orderDetails.filter(
            (detail) => detail.productId !== productId,
          )

          claim.order.productIds = claim.order.orderDetails.map((detail) => detail.productId)

          claim.order.price = claim.order.orderDetails.reduce(
            (sum, detail) => sum + detail.price * detail.quantity,
            0,
          )
          claim.order.totalPrice = calculateTotalPrice(claim)
        }
      }

      const claimToUpdate = state.allClaims.find((claim) => claim.id === claimId)
      if (claimToUpdate) {
        updateClaimOrder(claimToUpdate)
      }

      if (state.currentClaim.id === claimId) {
        updateClaimOrder(state.currentClaim)
      }
    },

    addCommentToProduct: (state, action) => {
      const { productId, comment } = action.payload

      if (state.currentClaim.order) {
        const productIndex = state.currentClaim.order.orderDetails.findIndex(
          (item) => item.productId === productId,
        )
        if (productIndex !== -1) {
          state.currentClaim.order.orderDetails[productIndex].comment = comment
        }
      }

      const claimToUpdate = state.allClaims.find((claim) => claim.id === state.currentClaim.id)
      if (claimToUpdate?.order) {
        const productIndex = claimToUpdate.order.orderDetails.findIndex(
          (item) => item.productId === productId,
        )
        if (productIndex !== -1) {
          claimToUpdate.order.orderDetails[productIndex].comment = comment
        }
      }
    },
    updateProductComment: (state, action) => {
      const { productId, comment } = action.payload

      if (state.currentClaim.order) {
        const productIndex = state.currentClaim.order.orderDetails.findIndex(
          (item) => item.productId === productId,
        )
        if (productIndex !== -1) {
          state.currentClaim.order.orderDetails[productIndex].comment = comment
        }
      }

      const claimToUpdate = state.allClaims.find((claim) => claim.id === state.currentClaim.id)
      if (claimToUpdate?.order) {
        const productIndex = claimToUpdate.order.orderDetails.findIndex(
          (item) => item.productId === productId,
        )
        if (productIndex !== -1) {
          claimToUpdate.order.orderDetails[productIndex].comment = comment
        }
      }
    },
    removeProductComment: (state, action) => {
      const { productId } = action.payload

      if (state.currentClaim.order) {
        const productIndex = state.currentClaim.order.orderDetails.findIndex(
          (item) => item.productId === productId,
        )
        if (productIndex !== -1) {
          delete state.currentClaim.order.orderDetails[productIndex].comment
        }
      }

      const claimToUpdate = state.allClaims.find((claim) => claim.id === state.currentClaim.id)
      if (claimToUpdate?.order) {
        const productIndex = claimToUpdate.order.orderDetails.findIndex(
          (item) => item.productId === productId,
        )
        if (productIndex !== -1) {
          delete claimToUpdate.order.orderDetails[productIndex].comment
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getAllClaims.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getAllClaims.fulfilled, (state, action) => {
      state.allClaims = action.payload
      state.isLoading = false
      state.error = ''
    })
    builder.addCase(getAllClaims.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload
    })
    builder.addCase(getPagedClaims.pending, (state) => {
      state.isLoadingPagedClaims = true
    })
    builder.addCase(getPagedClaims.fulfilled, (state, action) => {
      state.pagedClaims = action.payload
      state.isLoadingPagedClaims = false
      state.error = ''
    })
    builder.addCase(getPagedClaims.rejected, (state, action) => {
      state.isLoadingPagedClaims = false
      state.error = action.payload
    })

    builder.addCase(getStatsClaims.pending, (state) => {
      state.isLoadingStatsClaims = true
    })
    builder.addCase(getStatsClaims.fulfilled, (state, action) => {
      state.statsClaims = action.payload?.[0] || []
      state.isLoadingStatsClaims = false
      state.error = ''
    })
    builder.addCase(getStatsClaims.rejected, (state, action) => {
      state.isLoadingStatsClaims = false
      state.error = action.payload
    })

    builder.addCase(getAllClaimsFromPlatforms.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getAllClaimsFromPlatforms.fulfilled, (state, action) => {
      state.allClaimsFromPlaces = action.payload
      state.isLoading = false
      state.error = ''
    })
    builder.addCase(getAllClaimsFromPlatforms.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload
    })
    builder.addCase(getCurrentClaim.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getCurrentClaim.fulfilled, (state, action) => {
      state.currentClaim = action.payload
      state.isLoading = false
      state.error = ''
    })
    builder.addCase(getCurrentClaim.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload
    })
    builder.addCase(getCurrentClaimFromCurrentPlatform.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getCurrentClaimFromCurrentPlatform.fulfilled, (state, action) => {
      state.currentClaimFromCurrentPlace = action.payload
      state.isLoading = false
      state.error = ''
    })
    builder.addCase(getCurrentClaimFromCurrentPlatform.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload
    })

    builder.addCase(createClaim.rejected, (state, action) => {
      state.isLoading = false
    })
    builder.addCase(deleteCurrentClaim.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(deleteCurrentClaim.fulfilled, (state) => {
      state.isLoading = false
      state.error = ''
    })
    builder.addCase(deleteCurrentClaim.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload
    })
    builder.addCase(getClaimComments.pending, (state) => {
      state.isLoadingComments = true
    })
    builder.addCase(getClaimComments.fulfilled, (state, action) => {
      state.claimComments = action.payload
      state.isLoadingComments = false
      state.error = ''
    })
    builder.addCase(getClaimComments.rejected, (state, action) => {
      state.isLoadingComments = false
      state.error = action.payload
    })
    builder.addCase(createClaimComments.pending, (state) => {
      state.isLoadingComments = true
    })
    builder.addCase(createClaimComments.fulfilled, (state, action) => {
      state.isLoadingComments = false
      state.error = ''
    })
    builder.addCase(createClaimComments.rejected, (state, action) => {
      state.isLoadingComments = false
      state.error = action.payload
    })

    builder.addCase(changeClaimMainInformation.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(changeClaimMainInformation.fulfilled, (state, action) => {
      state.currentClaim = action.payload
      state.isLoading = false
      state.error = ''
    })
    builder.addCase(changeClaimMainInformation.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload
    })

    builder.addCase(changeClaimDescription.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(changeClaimDescription.fulfilled, (state, action) => {
      state.currentClaim.description = action.payload.description
      state.isLoading = false
      state.error = ''
    })
    builder.addCase(changeClaimDescription.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload
    })
  },
})

export const clearError = claimsSlice.actions.clearError
export const updateOrderDetailQuantity = claimsSlice.actions.updateOrderDetailQuantity
export const addOrderDetail = claimsSlice.actions.addOrderDetail
export const clearOrder = claimsSlice.actions.clearOrder
export const removeOrderDetail = claimsSlice.actions.removeOrderDetail
export const addCommentToProduct = claimsSlice.actions.addCommentToProduct
export const updateProductComment = claimsSlice.actions.updateProductComment
export const removeProductComment = claimsSlice.actions.removeProductComment

export const claimsReducer = claimsSlice.reducer
